import React, { useState, useEffect, useMemo } from 'react';
import { createUser, getAdminUsers, updateAdmin, deleteAdmin, toggleAdminStatus } from '../api/auth';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';
import { 
  PlusIcon, 
  XMarkIcon, 
  PencilSquareIcon, 
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import {
  ROLE_IDS,
  ROLE_CONFIG,
  getAssignableRoleOptions,
  getRoleBadgeClass,
} from '../config/adminRoles';

const canManageSystemUsers = (role) =>
  role === ROLE_IDS.SUPER_ADMIN || role === ROLE_IDS.ADMIN;

const SystemUsers = () => {
  const { user: loggedInUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const assignableRoles = getAssignableRoleOptions(loggedInUser?.role);
  const allowCreate = canManageSystemUsers(loggedInUser?.role);

  const roleOptionsForEdit = useMemo(() => {
    const base = getAssignableRoleOptions(loggedInUser?.role);
    if (!selectedUser?.role) return base;
    if (base.some((o) => o.value === selectedUser.role)) return base;
    return [
      ...base,
      {
        value: selectedUser.role,
        label: ROLE_CONFIG[selectedUser.role]?.label || selectedUser.role,
        description: ROLE_CONFIG[selectedUser.role]?.description,
      },
    ];
  }, [loggedInUser?.role, selectedUser]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .required('Password is required'),
    name: Yup.string()
      .required('Name is required'),
    role: Yup.string()
      .required('Role is required')
  });

  const updateValidationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters'),
    role: Yup.string()
      .required('Role is required')
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      name: '',
      role: ROLE_IDS.ADMIN,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await createUser(values);
        toast.success('User created successfully!');
        setIsModalOpen(false);
        resetForm();
        fetchUsers();
      } catch (err) {
        toast.error(err.message || 'Failed to create user');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const updateFormik = useFormik({
    initialValues: {
      name: '',
      password: '',
      role: ''
    },
    validationSchema: updateValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const updateData = { ...values };
        if (!updateData.password) {
          delete updateData.password;
        }
        await updateAdmin(selectedUser._id, updateData);
        toast.success('User updated successfully!');
        setIsUpdateModalOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } catch (err) {
        toast.error(err.message || 'Failed to update user');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getAdminUsers();
      setUsers(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = (user) => {
    setSelectedUser(user);
    updateFormik.setValues({
      name: user.name,
      password: '',
      role: user.role
    });
    setIsUpdateModalOpen(true);
  };

  const handleToggleStatus = async (user) => {
    try {
      await toggleAdminStatus(user._id);
      toast.success('User status updated successfully!');
      fetchUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to update user status');
    }
  };

  const handleDelete = async (user) => {    
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteAdmin(user._id);
        toast.success('User deleted successfully!');
        fetchUsers();
      } catch (error) {
        toast.error(error.message || 'Failed to delete user');
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);
  
  return (
    <div className="mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">System Users Management</h1>
        {allowCreate && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add System User
          </button>
        )}
      </div>

      {/* Users List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeClass(
                          user.role,
                        )}`}
                      >
                        {ROLE_CONFIG[user.role]?.label || user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {user.role === ROLE_IDS.SUPER_ADMIN ? (
                        <span className="text-gray-400 text-sm">—</span>
                      ) : (
                        allowCreate &&
                        String(user._id || user.id) !==
                          String(loggedInUser?.id) && (
                          <div className="flex justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => handleUpdate(user)}
                              className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                              title="Edit User"
                            >
                              <PencilSquareIcon className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(user)}
                              className={`inline-flex items-center p-2 border border-transparent rounded-md text-white ${
                                user.isActive
                                  ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                                  : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                              } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                              title={
                                user.isActive
                                  ? 'Deactivate User'
                                  : 'Activate User'
                              }
                            >
                              {user.isActive ? (
                                <XCircleIcon className="h-5 w-5" />
                              ) : (
                                <CheckCircleIcon className="h-5 w-5" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(user)}
                              className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                              title="Delete User"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">Add New System User</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={formik.handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                  className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                    formik.errors.name && formik.touched.name ? 'border-red-500' : 'border-gray-300'
                  } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                />
                {formik.errors.name && formik.touched.name && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.name}</div>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                  className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                    formik.errors.email && formik.touched.email ? 'border-red-500' : 'border-gray-300'
                  } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                />
                {formik.errors.email && formik.touched.email && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.email}</div>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.password}
                  className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                    formik.errors.password && formik.touched.password ? 'border-red-500' : 'border-gray-300'
                  } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                />
                {formik.errors.password && formik.touched.password && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.password}</div>
                )}
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.role}
                  className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                    formik.errors.role && formik.touched.role ? 'border-red-500' : 'border-gray-300'
                  } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                >
                  {assignableRoles.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {ROLE_CONFIG[formik.values.role] && (
                  <p className="mt-2 text-xs text-gray-500">
                    {ROLE_CONFIG[formik.values.role].description}
                  </p>
                )}
                {formik.errors.role && formik.touched.role && (
                  <div className="text-red-500 text-xs mt-1">{formik.errors.role}</div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {formik.isSubmitting ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    'Add User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update User Modal */}
      {isUpdateModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">Update System User</h3>
              <button
                onClick={() => {
                  setIsUpdateModalOpen(false);
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={updateFormik.handleSubmit} className="p-6 space-y-4">
              <div>
                <label htmlFor="update-name" className="block text-sm font-medium text-gray-700">
                  Name
                </label>
                <input
                  type="text"
                  id="update-name"
                  name="name"
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  value={updateFormik.values.name}
                  className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                    updateFormik.errors.name && updateFormik.touched.name ? 'border-red-500' : 'border-gray-300'
                  } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                />
                {updateFormik.errors.name && updateFormik.touched.name && (
                  <div className="text-red-500 text-xs mt-1">{updateFormik.errors.name}</div>
                )}
              </div>

              <div>
                <label htmlFor="update-password" className="block text-sm font-medium text-gray-700">
                  Password (leave blank to keep current)
                </label>
                <input
                  type="password"
                  id="update-password"
                  name="password"
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  value={updateFormik.values.password}
                  className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                    updateFormik.errors.password && updateFormik.touched.password ? 'border-red-500' : 'border-gray-300'
                  } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                />
                {updateFormik.errors.password && updateFormik.touched.password && (
                  <div className="text-red-500 text-xs mt-1">{updateFormik.errors.password}</div>
                )}
              </div>

              <div>
                <label htmlFor="update-role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  id="update-role"
                  name="role"
                  onChange={updateFormik.handleChange}
                  onBlur={updateFormik.handleBlur}
                  value={updateFormik.values.role}
                  className={`py-3 px-4 mt-1 block w-full rounded-md border-2 ${
                    updateFormik.errors.role && updateFormik.touched.role ? 'border-red-500' : 'border-gray-300'
                  } shadow-sm focus:ring-primary focus:border-primary sm:text-sm`}
                >
                  {roleOptionsForEdit.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {ROLE_CONFIG[updateFormik.values.role] && (
                  <p className="mt-2 text-xs text-gray-500">
                    {ROLE_CONFIG[updateFormik.values.role].description}
                  </p>
                )}
                {updateFormik.errors.role && updateFormik.touched.role && (
                  <div className="text-red-500 text-xs mt-1">{updateFormik.errors.role}</div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsUpdateModalOpen(false);
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateFormik.isSubmitting}
                  className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {updateFormik.isSubmitting ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    'Update User'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemUsers; 